import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "@/lib/api";
import { ChevronRight, X } from "lucide-react";

const TAB_KEYS = ["category", "room", "need", "budget", "feature"];
const TAB_LABELS = { category: "Categories", room: "Rooms", need: "Needs", budget: "Budget", feature: "Features" };

export default function MegaMenu({ open, onClose, taxonomyData }) {
  const [activeTab, setActiveTab] = useState("category");
  const [hoveredNode, setHoveredNode] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setHoveredNode(null); return; }
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => { document.removeEventListener("mousedown", handleClickOutside); document.removeEventListener("keydown", handleEsc); };
  }, [open, onClose]);

  if (!open || !taxonomyData) return null;

  const currentTax = taxonomyData.find(t => t.taxonomy_key === activeTab);
  // Build tree: roots + children
  const allNodes = currentTax?.nodes || [];
  const roots = allNodes.filter(n => !n.parent_id);
  const childrenOf = (pid) => allNodes.filter(n => n.parent_id === pid);

  return (
    <div className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-[2px]" data-testid="mega-menu-overlay">
      <div ref={ref}
        className="absolute top-14 sm:top-16 left-0 right-0 bg-white shadow-2xl border-b border-gray-200 max-h-[70vh] overflow-hidden animate-fade-in"
        data-testid="mega-menu-panel">
        {/* Tab bar */}
        <div className="border-b border-gray-100 bg-gray-50/80 px-6">
          <div className="max-w-[1440px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-0">
              {TAB_KEYS.map(key => (
                <button key={key} onClick={() => { setActiveTab(key); setHoveredNode(null); }}
                  data-testid={`mega-tab-${key}`}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                    activeTab === key ? "border-[#FFB800] text-[#1A1A1A]" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {TAB_LABELS[key] || key}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg text-gray-400" data-testid="mega-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1440px] mx-auto px-6 py-5">
          {(activeTab === "need" || activeTab === "budget" || activeTab === "feature") ? (
            // Flat grid for need/budget/feature
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {roots.map(node => (
                <Link key={node.node_id} to={`/browse?node=${node.node_id}`} onClick={onClose}
                  data-testid={`mega-node-${node.slug}`}
                  className="group p-4 rounded-xl border border-gray-100 hover:border-[#FFB800]/20 hover:bg-[#FFB800]/[0.02] transition-all">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-[#1A1A1A] mb-0.5">{node.name}</p>
                  {node.short_description && (
                    <p className="text-[11px] text-gray-400 line-clamp-2">{node.short_description}</p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            // Category/Room: three-column with subcategories + hero image
            <div className="flex gap-5 min-h-[300px]">
              {/* Left: root items */}
              <div className="w-52 flex-shrink-0 space-y-0.5 border-r border-gray-100 pr-3 overflow-y-auto max-h-[50vh]">
                {roots.map(node => {
                  const isCat = activeTab === "category";
                  return (
                    <Link key={node.node_id}
                      to={isCat ? `/category/${node.slug}` : `/browse?node=${node.node_id}`}
                      onClick={(e) => { e.preventDefault(); setHoveredNode(node); }}
                      onMouseEnter={() => setHoveredNode(node)}
                      data-testid={`mega-root-${node.slug}`}
                      className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-sm transition-all ${
                        hoveredNode?.node_id === node.node_id
                          ? "bg-[#FFB800]/5 text-[#1A1A1A] font-semibold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}>
                      {node.hero_image && (
                        <img src={node.hero_image} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <span className="flex-1 min-w-0 truncate">{node.name}</span>
                      <ChevronRight className="w-3.5 h-3.5 opacity-30 flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>

              {/* Center: subcategories + view all */}
              <div className="flex-1 min-w-0">
                {hoveredNode ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-bold text-gray-900">{hoveredNode.name}</h3>
                      <Link to={activeTab === "category" ? `/category/${hoveredNode.slug}` : `/browse?node=${hoveredNode.node_id}`} onClick={onClose}
                        className="text-xs font-medium text-[#1A1A1A] hover:underline flex items-center gap-1">
                        View All Products <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                    {hoveredNode.short_description && (
                      <p className="text-xs text-gray-500 mb-4">{hoveredNode.short_description}</p>
                    )}
                    {childrenOf(hoveredNode.node_id).length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {childrenOf(hoveredNode.node_id).map(child => (
                          <Link key={child.node_id}
                            to={activeTab === "category" ? `/category/${child.slug}` : `/browse?node=${child.node_id}`}
                            onClick={onClose}
                            data-testid={`mega-child-${child.slug}`}
                            className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]/20 group-hover:bg-[#FFB800] transition-colors flex-shrink-0" />
                            <span className="text-sm text-gray-700 group-hover:text-[#1A1A1A] transition-colors">{child.name}</span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link to={activeTab === "category" ? `/category/${hoveredNode.slug}` : `/browse?node=${hoveredNode.node_id}`}
                        onClick={onClose}
                        className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 bg-[#FFB800] text-white text-sm font-medium rounded-full hover:bg-[#2d2d4e] transition-colors">
                        Shop {hoveredNode.name} <ChevronRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    <p>Hover over a category to explore</p>
                  </div>
                )}
              </div>

              {/* Right: hero image */}
              {hoveredNode?.hero_image && (
                <div className="w-52 flex-shrink-0 hidden lg:block">
                  <Link to={activeTab === "category" ? `/category/${hoveredNode.slug}` : `/browse?node=${hoveredNode.node_id}`}
                    onClick={onClose} className="block group">
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4]">
                      <img src={hoveredNode.hero_image} alt={hoveredNode.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white text-xs font-bold">{hoveredNode.name}</p>
                        <p className="text-white/70 text-[10px] mt-0.5">Shop Now</p>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-100 bg-gray-50/80 px-6 py-3">
          <div className="max-w-[1440px] mx-auto flex items-center justify-between">
            <Link to="/categories" onClick={onClose}
              className="text-xs font-medium text-[#1A1A1A] hover:underline" data-testid="mega-all-categories">
              Browse all categories
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
